import React, { useState, useEffect } from 'react';
import { cloudAPI } from '@/services/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { CloudCog, Activity, Database } from 'lucide-react';
import { toast } from 'sonner';

const CloudLogsPage = () => {
  const [cloudTrailEvents, setCloudTrailEvents] = useState([]);
  const [azureEvents, setAzureEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const [cloudTrailRes, azureRes] = await Promise.all([
        cloudAPI.getCloudTrailEvents(),
        cloudAPI.getAzureEvents()
      ]);
      setCloudTrailEvents(cloudTrailRes.data.events || []);
      setAzureEvents(azureRes.data.events || []);
    } catch (error) {
      toast.error('Failed to load cloud events');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 md:p-8 lg:p-12" data-testid="cloud-logs-page">
      <div className="mb-8">
        <h1 className="text-4xl font-bold font-heading tracking-tight mb-2">Cloud Logs</h1>
        <p className="text-sm text-muted-foreground">Monitor events from AWS CloudTrail and Azure Monitor</p>
      </div>

      <Tabs defaultValue="cloudtrail" className="w-full">
        <TabsList>
          <TabsTrigger value="cloudtrail" data-testid="cloudtrail-tab">AWS CloudTrail</TabsTrigger>
          <TabsTrigger value="azure" data-testid="azure-tab">Azure Monitor</TabsTrigger>
        </TabsList>

        <TabsContent value="cloudtrail" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Database className="w-5 h-5" />
                AWS CloudTrail Events
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8">
                  <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
                </div>
              ) : cloudTrailEvents.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No CloudTrail events found</p>
              ) : (
                <div className="space-y-2">
                  {cloudTrailEvents.slice(0, 20).map((event, index) => (
                    <div key={index} className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition-colors">
                      <div className="flex-1">
                        <p className="font-medium text-sm">{event.event_name}</p>
                        <p className="text-xs text-muted-foreground">User: {event.username || 'N/A'}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-muted-foreground">{event.aws_region}</p>
                        <p className="text-xs text-muted-foreground">{new Date(event.event_time).toLocaleString()}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="azure" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CloudCog className="w-5 h-5" />
                Azure Monitor Events
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8">
                  <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
                </div>
              ) : azureEvents.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No Azure events found</p>
              ) : (
                <div className="space-y-2">
                  {azureEvents.slice(0, 20).map((event, index) => (
                    <div key={index} className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition-colors">
                      <div className="flex-1">
                        <p className="font-medium text-sm">{event.event_name}</p>
                        <p className="text-xs text-muted-foreground">Operation: {event.operation_name || 'N/A'}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-muted-foreground">Status: {event.status}</p>
                        <p className="text-xs text-muted-foreground">{new Date(event.event_time).toLocaleString()}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default CloudLogsPage;